import { Button, Dialog, Icon } from '@sr/ui-library';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { Steps } from 'react-daisyui';
import './AppMultiStepsModal.css';

export interface IAppMultiStepsModalProps {
  isOpen?: boolean;
  onCloseClick?: () => void;
  steps?: StepsContent[];
}

/**
 *
 * @param component contains page component
 * @param isLoading optional, show loader in action button and block closing the modal
 * @param nextButtonText optional, overwrite action button text
 * @param onNextClick optional, call back function that returns boolean if the logics success
 * @param previousButtonText optional, overwrite action button text
 * @param showPrevious optional, hide previous button
 * @param title step title
 */
type StepsContent = {
  classes?: string;
  component: ReactNode;
  isLoading?: boolean;
  nextButtonText?: string;
  onNextClick?: () => boolean;
  previousButtonText?: string;
  showPrevious?: boolean;
  title: string;
};

enum Go {
  Next = +1,
  Prev = -1,
}

/**
 *
 * @param isOpen Handles close/open of the modal base on given value
 * @param onCloseClick Callback, it return void function when cancel/close the modal
 * @param steps Dynamic object that contains component and title
 */
const AppMultiStepsModal = ({
  isOpen = false,
  onCloseClick,
  steps,
}: IAppMultiStepsModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const isCurrentStepLoading = !!steps?.at(currentStep)?.isLoading;

  const handleDirectionClick = (direction: Go) => {
    setCurrentStep(currentStep + direction);
  };

  const handleCloseModal = () => {
    // A loading step may still own in-flight work (e.g. client saves) whose
    // outcome it shows, so the X and Escape must not unmount it early.
    if (isCurrentStepLoading) return;

    setCurrentStep(0);
    onCloseClick?.();
  };

  return (
    <>
      <Dialog
        heading="Onboarding steps"
        className="modal-box min-h-[580px] max-w-3xl overflow-visible break-words px-4 py-11 md:px-16"
        hasActionButton={false}
        hasCancelButton={false}
        open={isOpen}
        showHeading={false}
        onOpenChange={handleCloseModal}
      >
        <div className="flex items-center justify-center">
          <Steps className="w-full md:w-3/4">
            {steps?.map((step, index) => (
              <Steps.Step
                key={step.title}
                className={clsx(
                  'relative',
                  currentStep === index ? 'step-active' : '',
                )}
                color={currentStep <= index ? 'secondary' : 'primary'}
                value="" // Set empty to prevent number as default value
              >
                <div className="absolute top-[13px] z-50">
                  {currentStep === index ? (
                    <Icon className="!text-sr-green" icon="circle" size="xs" />
                  ) : currentStep < index ? (
                    ''
                  ) : (
                    <Icon className="!text-sr-white" icon="check" size="xs" />
                  )}
                </div>
                <div
                  className={`text-xs font-normal ${
                    currentStep >= index
                      ? 'step-active-text'
                      : 'step-inactive-text opacity-50'
                  }`}
                >
                  {step.title}
                </div>
              </Steps.Step>
            ))}
          </Steps>
        </div>
        <div
          className={`relative flex h-96 items-center ${
            steps?.at(currentStep)?.classes
          }`}
        >
          {steps?.at(currentStep)?.component}
        </div>
        <div
          className={`flex flex-col items-center justify-center gap-4 pt-2.5 md:flex-row ${
            steps?.at(currentStep)?.classes
          }`}
        >
          {steps?.at(currentStep)?.showPrevious && (
            <Button
              disabled={currentStep < 1}
              variant="outline"
              onClick={() => handleDirectionClick(Go.Prev)}
            >
              {steps?.at(currentStep)?.previousButtonText ?? 'Go Back'}
            </Button>
          )}
          <Button
            disabled={isCurrentStepLoading}
            loading={isCurrentStepLoading}
            onClick={() => {
              // If onNextClick is define in steps content
              if (steps?.at(currentStep)?.onNextClick?.() != null) {
                const shouldProceed = steps?.at(currentStep)?.onNextClick?.();

                if (shouldProceed) handleDirectionClick(Go.Next);

                return;
              }

              // Close if last part of step or proceed to next step
              currentStep + 1 >= (steps?.length ?? 0)
                ? handleCloseModal()
                : handleDirectionClick(Go.Next);
            }}
          >
            {steps?.at(currentStep)?.nextButtonText ?? 'Next'}
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default AppMultiStepsModal;
